import { fetchClient } from '@/api/typedClient'
import { useTenantStore } from '@/stores/tenantStore'
import { CrossroadsMapping } from '@api/types'
import { GridApi, IRowNode } from 'ag-grid-community'
import { Modal, message } from 'antd'
import { useCallback } from 'react'
import { useCreateCrossroadsMapping } from '../../../hooks/useCreateCrossroadsMapping'

interface UseReferenceDataActionsProps {
  selectedObject: string
  refetch: () => void
}

interface UseReferenceDataActionsReturn {
  handleMappingUpdate: (
    newMapping: CrossroadsMapping,
    oldMapping: CrossroadsMapping,
    node: IRowNode<CrossroadsMapping>,
  ) => Promise<void>
  handleMappingDelete: (mapping: CrossroadsMapping, gridApi: GridApi) => void
  handleSync: () => Promise<void>
}

/**
 * Hook for managing CRUD operations and API calls in ReferenceDataPanel
 */
export const useMappingGridActions = ({
  selectedObject,
  refetch,
}: UseReferenceDataActionsProps): UseReferenceDataActionsReturn => {
  const _createMappingMutation = useCreateCrossroadsMapping()
  const { selectedTenant } = useTenantStore()

  // Shared handler for updating mappings (both parent and child rows)
  const handleMappingUpdate = useCallback(
    async (
      newMapping: CrossroadsMapping,
      oldMapping: CrossroadsMapping,
      node: IRowNode<CrossroadsMapping>,
    ) => {
      try {
        const id = oldMapping._id
        const { data, error } = await fetchClient.PUT(
          // @ts-expect-error TODO: Add /crossroads/mappings/{mapping_id} to OpenAPI spec
          `/crossroads/mappings/${id}`,
          { body: newMapping },
        )
        if (error) {
          throw new Error(`Failed to update mapping: ${JSON.stringify(error)}`)
        }
        node.setData(data as unknown as CrossroadsMapping)
      } catch (error) {
        console.error('Failed to update mapping:', error)

        // Revert the value on error if possible
        node.setData(oldMapping)
        throw error
      }
    },
    [],
  )

  // Handle delete with confirmation modal
  const handleMappingDelete = useCallback(
    (mapping: CrossroadsMapping, gridApi: GridApi) => {
      Modal.confirm({
        title: 'Delete Mapping',
        content: `Are you sure you want to delete "${mapping.display_name}"?`,
        okText: 'Delete',
        okType: 'danger',
        onOk: async () => {
          try {
            const { error } = await fetchClient.DELETE(
              // @ts-expect-error Dynamic endpoint not in OpenAPI spec
              `/crossroads/mappings/${mapping._id}`,
              {},
            )
            if (error) {
              throw new Error(
                `Failed to delete mapping: ${JSON.stringify(error)}`,
              )
            }
            message.success(`Deleted "${mapping.display_name}"`)
            gridApi.refreshServerSide({ purge: true })
          } catch (error) {
            console.error('Failed to delete mapping:', error)
            message.error('Failed to delete mapping')
          }
        },
      })
    },
    [],
  )

  // Handle sync operation
  const handleSync = useCallback(async () => {
    // Don't sync if Gravitate is selected
    if (selectedTenant?.name === 'Gravitate') {
      message.warning('Sync is not available for Gravitate tenant')
      return
    }

    if (!selectedTenant?.name) {
      message.warning('Please select a tenant to sync')
      return
    }

    try {
      message.loading('Syncing mappings...', 0)

      // Convert selectedObject to API type
      const objectTypeMap: Record<string, string> = {
        terminals: 'terminal',
        sites: 'site',
        products: 'product',
        counterparties: 'counterparty',
        tanks: 'tank',
      }
      const apiType = objectTypeMap[selectedObject] || selectedObject

      const { data, error } = await fetchClient.POST(
        '/crossroads/mappings/sync',
        {
          body: apiType as never,
        },
      )

      message.destroy()

      if (!error) {
        if (data?.timed_out) {
          message.info(
            'Sync is taking longer than expected and will continue in the background. Refresh in a few minutes to see updated mappings.',
            8,
          )
        } else {
          message.success('Mappings synced successfully')
        }
        // Refresh the data
        refetch()
      } else {
        message.error(`Failed to sync mappings: ${JSON.stringify(error)}`)
      }
    } catch (error) {
      message.destroy()
      console.error('Error syncing mappings:', error)
      message.error('Failed to sync mappings')
    }
  }, [selectedObject, selectedTenant, refetch])

  return {
    handleMappingUpdate,
    handleMappingDelete,
    handleSync,
  }
}
